import { Dispatch, FC, SetStateAction, useContext } from "react";
import { Button, Stack } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateField, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import __ from "src/utils/Translations";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import { calendarExcludeDaySchema } from "src/Pages/Company/Absences/Calendars/Calendar/CalendarExcludeDayForm/CalendarExcludeDayForm.schema";

interface ICalendarExcludeDayFormProps {
  day: Date;
  setExcludedDays: Dispatch<SetStateAction<IExcludedDay[]>>;
}

interface IFormValues {
  date: Date;
  label: string;
}

const CalendarExcludeDayForm: FC<ICalendarExcludeDayFormProps> = ({
  day,
  setExcludedDays,
}) => {
  const { resetModal } = useContext(GlobalModalContext);
  const formMethods = useForm({
    resolver: zodResolver(calendarExcludeDaySchema),
    mode: "onChange",
    defaultValues: {
      date: day,
      label: "",
    },
  });

  const { control, handleSubmit } = formMethods;

  const onSubmit = (formData: IFormValues) => {
    const newDate = format(formData.date, "yyyy-MM-dd");

    setExcludedDays((prevState) => {
      // Remove existing entry with the same date if it exists
      const filteredState = prevState.filter((day) => day.date !== newDate);

      return [
        ...filteredState,
        {
          date: newDate,
          label: formData.label,
        },
      ];
    });

    resetModal();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <FormProvider {...formMethods}>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          data-testid="calendar-exclude-day-form"
        >
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <DateField
                {...field}
                id="date"
                label={__("Data")}
                required
                fullWidth
                readOnly
                margin="normal"
                inputRef={field.ref}
                data-testid="calendar-exclude-day-form-field__date"
              />
            )}
          />
          <ControlledTextField
            name="label"
            label={__("Dodaj nazwę dnia wolnego")}
            placeholder={__("Wpisz nazwę")}
            required
            fullWidth
            maxLength={100}
            margin="normal"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            data-testid="calendar-exclude-day-form-field__label"
            normalizeSpaces
          />
          <Stack direction="row" sx={{ gap: 2, width: "100%" }}>
            <Button
              variant="outlined"
              type="button"
              size="large"
              fullWidth
              onClick={() => resetModal()}
            >
              {__("Anuluj")}
            </Button>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="large"
              data-testid="calendar-exclude-day-form__button-submit"
            >
              {__("Zapisz")}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </LocalizationProvider>
  );
};

export default CalendarExcludeDayForm;
